// src/pages/driverReports/DriverAssignmentCostSetup.js

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import {
  applyAssignmentCostPlanForMonth,
  currentMonthText,
  getAssignmentCostPlanHistory,
  saveAssignmentCostPlanRevision,
} from "./assignmentCostPlanHistory";

function toDate(value) {
  try {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function dateText(value) {
  const d = toDate(value);
  if (!d) return "-";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function monthText(value) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthStart(month) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1, 0, 0, 0, 0);
}

function monthEnd(month) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m, 0, 23, 59, 59, 999);
}

function overlapsMonth(a, month) {
  const start = toDate(a?.startDate);
  const end = toDate(a?.endDate) || new Date(8640000000000000);
  const ms = monthStart(month);
  const me = monthEnd(month);
  if (!start || !ms || !me) return false;
  return start <= me && end >= ms;
}

function norm(value) {
  return String(value || "").trim().toLowerCase();
}

function rowStyle(status) {
  if (status === "missing") return { background: "#fff7ed" };
  if (status === "configured") return { background: "#f0fdf4" };
  return {};
}

function monthIndex(month) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return null;
  return y * 12 + (m - 1);
}

function monthDiff(fromMonth, toMonth) {
  const from = monthIndex(fromMonth);
  const to = monthIndex(toMonth);
  if (from === null || to === null) return 0;
  return to - from;
}

function planText(a, statementMonth) {
  const enabled =
    a?.installmentPlanEnabled === true ||
    Number(a?.installmentTotalCount || 0) > 0 ||
    Number(a?.installmentCurrentNo || 0) > 0;

  if (!enabled) return "";

  const baseNo = Number(a.installmentCurrentNo || 0);
  const total = Number(a.installmentTotalCount || 0);
  const baseMonth =
    a.installmentBaseMonth || monthText(a.installmentEffectiveFrom || a.startDate);

  if (!baseNo || !baseMonth) return "";

  const calculatedNo = statementMonth
    ? baseNo + monthDiff(baseMonth, statementMonth)
    : baseNo;

  return total
    ? `Installment ${calculatedNo}/${total} (base ${baseNo}/${total} in ${baseMonth})`
    : `Installment ${calculatedNo} (base ${baseNo} in ${baseMonth})`;
}

function defaultBaseMonth(a, fallbackMonth = "") {
  return (
    a?.installmentBaseMonth ||
    monthText(a?.installmentEffectiveFrom || a?.startDate) ||
    fallbackMonth ||
    ""
  );
}

function roleText(a) {
  if (a?.driverRole === "second") return "Second";
  if (a?.driverRole === "main") return "Main";
  return "Not set";
}

function money(value) {
  return Number(value || 0).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function historyPlanSummary(plan) {
  if (!plan?.costConfigured) return "Old logic / not configured";
  if (plan.driverRole === "second") {
    return `Second driver — Extra AED ${money(plan.secondDriverMonthlyExpense || 500)} / month`;
  }

  const baseNo = Number(plan.installmentCurrentNo || 0);
  const total = Number(plan.installmentTotalCount || 0);
  const installment = plan.installmentPlanEnabled
    ? total
      ? ` — Installment base ${baseNo}/${total} in ${plan.installmentBaseMonth || "-"}`
      : ` — Installment base ${baseNo} in ${plan.installmentBaseMonth || "-"}`
    : "";

  return `Main driver — AED ${money(plan.monthlyInstallment)} / month${installment}`;
}

export default function DriverAssignmentCostSetup() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statementMonth, setStatementMonth] = useState(currentMonthText());
  const [showMonthOnly, setShowMonthOnly] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [historyOpenId, setHistoryOpenId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    effectiveFromMonth: "",
    driverRole: "main",
    monthlyInstallment: "",
    secondDriverMonthlyExpense: "500",
    costConfigured: true,
    installmentPlanEnabled: false,
    installmentCurrentNo: "",
    installmentTotalCount: "",
    installmentBaseMonth: "",
  });

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const qRef = query(
        collection(db, "driverCarAssignments"),
        orderBy("startDate", "desc")
      );
      const snap = await getDocs(qRef);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAssignments(list);
    } catch (err) {
      console.error("Failed to load driver assignments:", err);
      alert("Failed to load assignments: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, []);

  const filtered = useMemo(() => {
    const q = norm(search);
    return assignments.filter((a) => {
      // Statement preparation must include assignments that ended during the
      // selected month, so month overlap is used instead of only active rows.
      if (showMonthOnly && statementMonth && !overlapsMonth(a, statementMonth)) {
        return false;
      }
      if (!q) return true;
      return [a.driverName, a.vehicleNumber, a.company, a.vehicleType]
        .map(norm)
        .some((x) => x.includes(q));
    });
  }, [assignments, search, showMonthOnly, statementMonth]);

  const filteredForMonth = useMemo(
    () => filtered.map((a) => applyAssignmentCostPlanForMonth(a, statementMonth)),
    [filtered, statementMonth]
  );

  const startEdit = (a) => {
    const monthAssignment = applyAssignmentCostPlanForMonth(a, statementMonth);
    const role =
      String(monthAssignment?.driverRole || "main").toLowerCase() === "second"
        ? "second"
        : "main";
    const planEnabled =
      monthAssignment?.installmentPlanEnabled === true ||
      Number(monthAssignment?.installmentCurrentNo || 0) > 0 ||
      Number(monthAssignment?.installmentTotalCount || 0) > 0;

    setEditingId(a.id);
    setForm({
      // The selected statement month is the safe default for a revision.
      // The user can choose another month before saving.
      effectiveFromMonth: statementMonth || monthText(a.startDate),
      driverRole: role,
      monthlyInstallment: monthAssignment?.monthlyInstallment
        ? String(monthAssignment.monthlyInstallment)
        : "",
      secondDriverMonthlyExpense: monthAssignment?.secondDriverMonthlyExpense
        ? String(monthAssignment.secondDriverMonthlyExpense)
        : "500",
      costConfigured: monthAssignment?.costConfigured === false ? false : true,
      installmentPlanEnabled: planEnabled,
      installmentCurrentNo: monthAssignment?.installmentCurrentNo
        ? String(monthAssignment.installmentCurrentNo)
        : "",
      installmentTotalCount: monthAssignment?.installmentTotalCount
        ? String(monthAssignment.installmentTotalCount)
        : "",
      installmentBaseMonth:
        monthAssignment?.installmentBaseMonth ||
        defaultBaseMonth(monthAssignment, statementMonth),
    });
  };

  const saveEdit = async (a) => {
    if (saving) return;

    try {
      const role = form.driverRole === "second" ? "second" : "main";
      const configured = Boolean(form.costConfigured);
      const monthly = Number(form.monthlyInstallment || 0);
      const secondMonthly = Number(form.secondDriverMonthlyExpense || 500);
      const planEnabled =
        role === "main" && configured && Boolean(form.installmentPlanEnabled);
      const currentNo = Number(form.installmentCurrentNo || 0);
      const totalCount = Number(form.installmentTotalCount || 0);
      const baseMonth = String(form.installmentBaseMonth || "").trim();
      const effectiveFromMonth = String(form.effectiveFromMonth || "").trim();

      if (!effectiveFromMonth) {
        alert("Please select the month from which this cost plan should apply.");
        return;
      }

      if (configured && role === "main" && monthly <= 0) {
        alert("Please enter monthly installment for main driver, or untick Cost Configured.");
        return;
      }

      if (configured && role === "second" && secondMonthly <= 0) {
        alert("Please enter second-driver monthly expense, or untick Cost Configured.");
        return;
      }

      if (planEnabled) {
        if (!currentNo || currentNo <= 0) {
          alert("Please enter the current installment number, e.g. 1 or 25.");
          return;
        }
        if (!totalCount || totalCount <= 0) {
          alert("Please enter total installments, e.g. 24 or 36.");
          return;
        }
        if (!baseMonth) {
          alert("Please select the base month for this installment number.");
          return;
        }
      }

      const ok = window.confirm(
        `Save this cost plan from ${effectiveFromMonth}?\n\n` +
          `Months before ${effectiveFromMonth} will keep their previous plan/history. ` +
          `Rebuilding an old car sheet will therefore continue to use the old amount/installment.`
      );
      if (!ok) return;

      setSaving(true);

      await saveAssignmentCostPlanRevision({
        assignmentId: a.id,
        effectiveFromMonth,
        values: {
          driverRole: role,
          costConfigured: configured,
          monthlyInstallment: role === "main" && configured ? monthly : 0,
          secondDriverMonthlyExpense:
            role === "second" && configured ? secondMonthly : 500,
          installmentPlanEnabled: planEnabled,
          installmentCurrentNo: planEnabled ? currentNo : 0,
          installmentTotalCount: planEnabled ? totalCount : 0,
          installmentBaseMonth: planEnabled ? baseMonth : "",
        },
      });

      setEditingId(null);
      await loadAssignments();
      alert(
        `Cost plan saved from ${effectiveFromMonth}. Previous months remain preserved in history. ` +
          `Refresh/rebuild the affected statement month to recalculate an already-generated car sheet.`
      );
    } catch (err) {
      console.error("Failed to save cost setup:", err);
      alert("Failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const configuredCount = filteredForMonth.filter((a) => a.costConfigured).length;
  const missingCount = filteredForMonth.length - configuredCount;

  return (
    <div style={{ padding: 20, maxWidth: 1500 }}>
      <h2>Driver Assignment Cost Setup</h2>
      <p style={{ color: "#555", maxWidth: 1100 }}>
        Use this screen for assignment monthly costs. Select the statement month to see the cost
        plan that applies to that month. When a contract/installment changes, save the new plan
        from its effective month; previous months remain in history and are reused if an old car
        sheet is refreshed or rebuilt.
      </p>

      <div
        style={{
          padding: 10,
          marginBottom: 14,
          border: "1px solid #bfdbfe",
          background: "#eff6ff",
          color: "#1e3a8a",
          fontSize: 13,
          borderRadius: 6,
          maxWidth: 1100,
        }}
      >
        Plan renewal does not end the driver-car assignment. Example: August can remain 36/36 at
        the old amount, while a new plan starts in September as 1/24 at the revised amount. Car
        sheet balance carry-forward stays on the same assignment/driver continuity.
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <label>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Statement month</div>
          <input
            type="month"
            value={statementMonth}
            onChange={(e) => setStatementMonth(e.target.value)}
            style={{ padding: 8, minWidth: 150 }}
          />
        </label>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search car / driver / company"
          style={{ padding: 8, minWidth: 280, marginTop: 18 }}
        />
        <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 18 }}>
          <input
            type="checkbox"
            checked={showMonthOnly}
            onChange={(e) => setShowMonthOnly(e.target.checked)}
          />
          Show assignments in selected month only
        </label>
        <button
          type="button"
          onClick={loadAssignments}
          disabled={loading}
          style={{ marginTop: 18 }}
        >
          {loading ? "Loading..." : "Reload"}
        </button>
      </div>

      <div style={{ marginBottom: 12, fontSize: 13 }}>
        Showing <strong>{filtered.length}</strong> assignments | Configured for {statementMonth}:{" "}
        <strong>{configuredCount}</strong> | Not configured: <strong>{missingCount}</strong>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f3f4f6" }}>
              <th style={{ textAlign: "left", padding: 8 }}>Car</th>
              <th style={{ textAlign: "left", padding: 8 }}>Driver</th>
              <th style={{ textAlign: "left", padding: 8 }}>Start</th>
              <th style={{ textAlign: "left", padding: 8 }}>End</th>
              <th style={{ textAlign: "left", padding: 8 }}>Role</th>
              <th style={{ textAlign: "left", padding: 8 }}>Monthly cost / plan</th>
              <th style={{ textAlign: "left", padding: 8 }}>Status</th>
              <th style={{ textAlign: "left", padding: 8 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const viewAssignment = applyAssignmentCostPlanForMonth(a, statementMonth);
              const isEditing = editingId === a.id;
              const status = viewAssignment.costConfigured ? "configured" : "missing";
              const existingPlanText = planText(viewAssignment, statementMonth);
              const history = getAssignmentCostPlanHistory(a);
              const historyOpen = historyOpenId === a.id;

              return (
                <React.Fragment key={a.id}>
                  <tr style={rowStyle(status)}>
                    <td style={cellStyle}>{a.vehicleNumber || "-"}</td>
                    <td style={cellStyle}>{a.driverName || "-"}</td>
                    <td style={cellStyle}>{dateText(a.startDate)}</td>
                    <td style={cellStyle}>{a.endDate ? dateText(a.endDate) : "Present"}</td>
                    <td style={cellStyle}>
                      {isEditing ? (
                        <select
                          value={form.driverRole}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, driverRole: e.target.value }))
                          }
                        >
                          <option value="main">Main Driver</option>
                          <option value="second">Second Driver</option>
                        </select>
                      ) : (
                        roleText(viewAssignment)
                      )}
                    </td>
                    <td style={{ ...cellStyle, minWidth: 330 }}>
                      {isEditing ? (
                        <>
                          <div
                            style={{
                              padding: 8,
                              marginBottom: 8,
                              background: "#f8fafc",
                              border: "1px solid #cbd5e1",
                              borderRadius: 5,
                            }}
                          >
                            <label style={{ display: "block", fontWeight: 700, marginBottom: 4 }}>
                              Apply this plan from month
                            </label>
                            <input
                              type="month"
                              value={form.effectiveFromMonth}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  effectiveFromMonth: e.target.value,
                                }))
                              }
                              style={{ padding: 6 }}
                            />
                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                              Earlier months stay unchanged in history. A later saved plan, if any,
                              also stays in place.
                            </div>
                          </div>

                          <label style={{ display: "block", marginBottom: 6 }}>
                            <input
                              type="checkbox"
                              checked={form.costConfigured}
                              onChange={(e) =>
                                setForm((f) => ({ ...f, costConfigured: e.target.checked }))
                              }
                            />{" "}
                            Cost configured
                          </label>

                          {form.driverRole === "main" ? (
                            <div style={{ display: "grid", gap: 8 }}>
                              <input
                                type="number"
                                value={form.monthlyInstallment}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    monthlyInstallment: e.target.value,
                                  }))
                                }
                                placeholder="Monthly installment"
                                style={{ padding: 6, width: 180 }}
                              />

                              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={form.installmentPlanEnabled}
                                  onChange={(e) =>
                                    setForm((f) => ({
                                      ...f,
                                      installmentPlanEnabled: e.target.checked,
                                    }))
                                  }
                                />
                                Track installment number, e.g. 1/24 or 36/36
                              </label>

                              {form.installmentPlanEnabled && (
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr",
                                    gap: 6,
                                  }}
                                >
                                  <input
                                    type="number"
                                    step="0.5"
                                    value={form.installmentCurrentNo}
                                    onChange={(e) =>
                                      setForm((f) => ({
                                        ...f,
                                        installmentCurrentNo: e.target.value,
                                      }))
                                    }
                                    placeholder="Current no."
                                    style={{ padding: 6 }}
                                  />
                                  <input
                                    type="number"
                                    step="0.5"
                                    value={form.installmentTotalCount}
                                    onChange={(e) =>
                                      setForm((f) => ({
                                        ...f,
                                        installmentTotalCount: e.target.value,
                                      }))
                                    }
                                    placeholder="Total"
                                    style={{ padding: 6 }}
                                  />
                                  <input
                                    type="month"
                                    value={form.installmentBaseMonth}
                                    onChange={(e) =>
                                      setForm((f) => ({
                                        ...f,
                                        installmentBaseMonth: e.target.value,
                                      }))
                                    }
                                    style={{ padding: 6 }}
                                  />
                                </div>
                              )}

                              {form.installmentPlanEnabled && (
                                <div style={{ fontSize: 12, color: "#666" }}>
                                  For a renewed September plan, enter current no. 1, total 24, and
                                  base month September. August remains on the previous plan.
                                </div>
                              )}
                            </div>
                          ) : (
                            <input
                              type="number"
                              value={form.secondDriverMonthlyExpense}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  secondDriverMonthlyExpense: e.target.value,
                                }))
                              }
                              placeholder="500"
                              style={{ padding: 6, width: 150 }}
                            />
                          )}
                        </>
                      ) : viewAssignment.costConfigured ? (
                        viewAssignment.driverRole === "second" ? (
                          `Extra AED ${Number(
                            viewAssignment.secondDriverMonthlyExpense || 500
                          ).toFixed(2)} / month`
                        ) : (
                          <div>
                            <div>{`AED ${Number(viewAssignment.monthlyInstallment || 0).toFixed(
                              2
                            )} / month`}</div>
                            {existingPlanText && (
                              <div style={{ color: "#555", fontSize: 12 }}>
                                {existingPlanText}
                              </div>
                            )}
                            {viewAssignment.activeCostPlan && (
                              <div style={{ color: "#1d4ed8", fontSize: 11 }}>
                                Versioned plan from {viewAssignment.activeCostPlan.effectiveFromMonth}
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        "Old logic / not configured"
                      )}
                    </td>
                    <td style={cellStyle}>
                      {viewAssignment.costConfigured ? "Configured" : "Not configured"}
                    </td>
                    <td style={cellStyle}>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button type="button" onClick={() => saveEdit(a)} disabled={saving}>
                            {saving ? "Saving..." : "Save from month"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button type="button" onClick={() => startEdit(a)}>
                            Configure
                          </button>
                          <button
                            type="button"
                            onClick={() => setHistoryOpenId(historyOpen ? null : a.id)}
                          >
                            {historyOpen ? "Hide History" : `History${history.length ? ` (${history.length})` : ""}`}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>

                  {historyOpen && (
                    <tr>
                      <td colSpan={8} style={{ padding: 10, background: "#f8fafc" }}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>
                          Cost plan history — {a.vehicleNumber || "-"} / {a.driverName || "-"}
                        </div>

                        {history.length === 0 ? (
                          <div style={{ color: "#64748b", fontSize: 12 }}>
                            No month-versioned history has been created yet. This assignment still
                            uses its existing legacy cost fields. The first month-based revision will
                            preserve the prior configured setup automatically when required.
                          </div>
                        ) : (
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              fontSize: 12,
                              background: "white",
                            }}
                          >
                            <thead>
                              <tr>
                                <th style={historyTh}>From</th>
                                <th style={historyTh}>To</th>
                                <th style={historyTh}>Plan</th>
                              </tr>
                            </thead>
                            <tbody>
                              {history.map((plan) => (
                                <tr key={plan.effectiveFromMonth}>
                                  <td style={historyTd}>{plan.effectiveFromMonth}</td>
                                  <td style={historyTd}>{plan.effectiveToMonth || "Present / next revision"}</td>
                                  <td style={historyTd}>{historyPlanSummary(plan)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const cellStyle = {
  borderBottom: "1px solid #e5e7eb",
  padding: 8,
  verticalAlign: "top",
};

const historyTh = {
  textAlign: "left",
  padding: 7,
  borderBottom: "1px solid #cbd5e1",
  background: "#f1f5f9",
};

const historyTd = {
  padding: 7,
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "top",
};
